import React from 'react'
import PropTypes from 'prop-types'
import HighCharts from 'highcharts'
import Chart from 'highcharts-react-official'
import exportToImage from '../../modules/utils/exports'
import { formatAmount, formatPercentage, minFormatter } from '../../modules/utils/formatters'
import theme from '../../config/styles/theme'
import { Exportable, GlobalStyle } from './ColumnChart.styles'

/**
 * The ColumnChart's component.
 */
const ColumnChart = props => {
  const { categories, data, distValue, fileName, id, inputColumnChartRef } = props
  const options = {
    accessibility: { enabled: false },
    chart: {
      animation: false,
      height: 400,
      type: 'column'
    },
    plotOptions: {
      column: { minPointLength: 10, pointWidth: 40 },
      series: {
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          style: {
            color: theme.palette.brand.black1,
            fontSize: '8px',
            textOutline: 'none'
          },
          formatter: function () {
            if (distValue === 'amount') {
              return `$${minFormatter(this.y || 0)}`
            } else if (distValue === 'percentage') {
              return formatPercentage(this.y)
            }

            return minFormatter(this.y || 0)
          }
        },
        events: {
          afterAnimate: function () {
            this.points.forEach(point => {
              if (point.y === 0) {
                point.graphic?.element?.setAttribute('height', 0)
                point.dataLabel?.element?.firstChild?.setAttribute('y', 24)
              } else {
                point.dataLabel?.element?.firstChild?.setAttribute('y', 14)
              }
            })
          }
        }
      }
    },
    series: data,
    title: { text: '' },
    tooltip: {
      formatter: function () {
        if (distValue === 'amount') {
          return `${this.series.name} : <strong>$${formatAmount(this.y || 0)}</strong>`
        } else if (distValue === 'oc') {
          return `${this.series.name} : <strong>${this.point.y}</strong> órdenes de compra`
        } else if (distValue === 'percentage') {
          return `${this.series.name} : <strong>${formatPercentage(this.point.y)}</strong>`
        }

        return `${this.series.name} : <strong>${this.point.y}</strong> licitaciones`
      }
    },
    yAxis: {
      title: { text: '' }
    },
    xAxis: {
      categories,
      endOnTick: true,
      gridLineDashStyle: 'ShortDash',
      gridLineWidth: 1,
      min: 0,
      startOnTick: false
    }
  }
  const exportData = () => exportToImage(id, fileName)

  return (
    <>
      <GlobalStyle />
      <Exportable id={id}>
        <Chart highcharts={HighCharts} options={options} />
      </Exportable>
      <Exportable ref={inputColumnChartRef} onClick={exportData} />
    </>
  )
}

ColumnChart.defaultProps = {
  id: 'columnChart'
}

ColumnChart.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.oneOfType([
    PropTypes.shape({
      amount: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          data: PropTypes.arrayOf(PropTypes.number)
        })
      ),
      biddings: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          data: PropTypes.arrayOf(PropTypes.number)
        })
      ),
      orders: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          data: PropTypes.arrayOf(PropTypes.number)
        })
      )
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        color: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
        name: PropTypes.string
      })
    )
  ]),
  distValue: PropTypes.string,
  fileName: PropTypes.string,
  id: PropTypes.string,
  inputColumnChartRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(window.HTMLDivElement) })
  ])
}

export default ColumnChart
